import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { Language } from '../constants'
import { CustomTemplate } from '../services/models/CustomTemplate.model'
import { AppPlanId } from '../services/models/Subscription.model'
import { Visit } from '../services/models/Visit.model'

const BASE_HREF = ''

enum ROUTE {
    MAIN = '/',
    AUTHCHECK = '/auth',
    SIGNIN = '/auth/signin',
    SIGNUP = '/auth/signup',
    SIGNUP_COMPLETION = '/auth/signup/complete/:code?',
    FORGOT_PASSWORD = '/auth/forgot-password',
    RESET_PASSWORD = '/auth/reset-password/:code',
    VISITS = '/visits',
    VISIT = '/visits/:id',
    VISIT_START = '/visits/start',
    VISIT_RECORD = '/visits/:id/record',
    DICTATIONS = '/dictations',
    DICTATION_RECORD = '/dictations/:id/record',
    SETTINGS = '/settings',
    BILLING = '/settings/billing',
    BILLING_UPDATE = '/settings/billing/update',
    BILLING_CHECKOUT = '/settings/billing/checkout/:appPlanId',
    REFERRAL = '/settings/referral',
    ACCOUNT = '/settings/account',
    TEMPLATES = '/settings/templates',
    TEMPLATE_FORM = '/settings/templates/:id',
    DEVELOPER = '/settings/developer',
    REQUEST_FEATURE = '/settings/request-feature',
    UNAUTHORIZED = '/unauthorized',
    SNIPPETS = '/settings/snippets'
}

const authCheckRoute = `${BASE_HREF}${ROUTE.AUTHCHECK}`
const signInRoute = `${BASE_HREF}${ROUTE.SIGNIN}`
const signUpRoute = `${BASE_HREF}${ROUTE.SIGNUP}`
const signUpCompletionRoute = `${BASE_HREF}${ROUTE.SIGNUP_COMPLETION}`
const forgotPasswordRoute = `${BASE_HREF}${ROUTE.FORGOT_PASSWORD}`
const resetPasswordRoute = `${BASE_HREF}${ROUTE.RESET_PASSWORD}`
const mainRoute = `${BASE_HREF}${ROUTE.MAIN}`
const visitsRoute = `${BASE_HREF}${ROUTE.VISITS}`
const visitRoute = `${BASE_HREF}${ROUTE.VISIT}`
const visitStartRoute = `${BASE_HREF}${ROUTE.VISIT_START}`
const visitRecordRoute = `${BASE_HREF}${ROUTE.VISIT_RECORD}`
const dictationsRoute = `${BASE_HREF}${ROUTE.DICTATIONS}`
const dictationRecordRoute = `${BASE_HREF}${ROUTE.DICTATION_RECORD}`
const settingsRoute = `${BASE_HREF}${ROUTE.SETTINGS}`
const billingRoute = `${BASE_HREF}${ROUTE.BILLING}`
const billingUpdateRoute = `${BASE_HREF}${ROUTE.BILLING_UPDATE}`
const billingCheckoutRoute = `${BASE_HREF}${ROUTE.BILLING_CHECKOUT}`
const referralRoute = `${BASE_HREF}${ROUTE.REFERRAL}`
const accountRoute = `${BASE_HREF}${ROUTE.ACCOUNT}`
const templatesRoute = `${BASE_HREF}${ROUTE.TEMPLATES}`
const templateFormRoute = `${BASE_HREF}${ROUTE.TEMPLATE_FORM}`
const developerRoute = `${BASE_HREF}${ROUTE.DEVELOPER}`
const requestFeatureRoute = `${BASE_HREF}${ROUTE.REQUEST_FEATURE}`
const unauthorizedRoute = `${BASE_HREF}${ROUTE.UNAUTHORIZED}`
const snippetsRoute = `${BASE_HREF}${ROUTE.SNIPPETS}`

const publicRoutes = [
    ROUTE.SIGNIN,
    ROUTE.SIGNUP,
    (() => ROUTE.SIGNUP_COMPLETION.replace(':code?', ''))(),
    ROUTE.FORGOT_PASSWORD,
    (() => ROUTE.RESET_PASSWORD.replace(':code', ''))()
]

export const useRoute = () => {
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const isProtectedRoute = useMemo(() => (publicRoutes.find((route) => pathname.startsWith(route)) ? false : true), [pathname])

    const navAuthCheck = useCallback(() => navigate(authCheckRoute), [navigate])
    const navSignIn = useCallback((state: any) => navigate(signInRoute, { state }), [navigate])
    const navSignUp = useCallback(() => navigate(signUpRoute), [navigate])
    const navSignUpCompletion = useCallback(
        (code: string = '', state: any) => {
            const route = signUpCompletionRoute.replace(':code?', code)
            navigate(route, { state })
        },
        [navigate]
    )
    const navForgotPassword = useCallback(() => navigate(forgotPasswordRoute), [navigate])
    const navResetPassword = useCallback(
        (code: string) => {
            const route = resetPasswordRoute.replace(':code', code)
            navigate(route)
        },
        [navigate]
    )
    const navMain = useCallback(() => navigate(mainRoute), [navigate])
    const navVisits = useCallback(() => navigate(visitsRoute), [navigate])
    const navVisit = useCallback(
        (id: string, visit?: Visit) => {
            const route = visitRoute.replace(':id', id)
            navigate(route, {
                state: {
                    visit
                }
            })
        },
        [navigate]
    )
    const navVisitStart = useCallback(() => navigate(visitStartRoute), [navigate])
    const navVisitRecord = useCallback(
        (id: string, visit?: Visit, startRecordingOnLoad?: boolean, templateId?: string, customTemplateId?: string, languageSource?: Language, languageTarget?: Language) => {
            const route = visitRecordRoute.replace(':id', id)
            navigate(route, {
                state: {
                    visit,
                    startRecordingOnLoad,
                    templateId,
                    customTemplateId,
                    languageSource,
                    languageTarget
                }
            })
        },
        [navigate]
    )
    const navDictationRecord = useCallback(
        (id: string, dictation: Visit, startRecordingOnLoad: boolean = false) => {
            const route = dictationRecordRoute.replace(':id', id)
            navigate(route, {state: {dictation, startRecordingOnLoad}})
        },
        [navigate]
    )
    const navSettings = useCallback(() => navigate(settingsRoute), [navigate])
    const navBilling = useCallback(() => navigate(billingRoute), [navigate])
    const navReferral = useCallback(()=> navigate(referralRoute), [navigate])
    const navBillingUpdate = useCallback(() => navigate(billingUpdateRoute), [navigate])
    const navBillingCheckout = useCallback((appPlanId: AppPlanId) => navigate(billingCheckoutRoute.replace(':appPlanId', appPlanId)), [navigate])
    const navAccount = useCallback(() => navigate(accountRoute), [navigate])
    const navTemplates = useCallback(() => navigate(templatesRoute), [navigate])
    const navTemplateForm = useCallback(
        (id: string, customTemplate?: CustomTemplate) =>
            navigate(templateFormRoute.replace(':id', id), {
                state: {
                    customTemplate
                }
            }),
        [navigate]
    )
    const navDeveloper = useCallback(() => navigate(developerRoute), [navigate])
    const navRequestFeature = useCallback(() => navigate(requestFeatureRoute), [navigate])
    const navUnauthorized = useCallback(() => navigate(unauthorizedRoute), [navigate])
    const navSnippets = useCallback(() => navigate(snippetsRoute), [navigate])

    return {
        authCheckRoute,
        signInRoute,
        signUpRoute,
        signUpCompletionRoute,
        forgotPasswordRoute,
        resetPasswordRoute,
        mainRoute,
        visitsRoute,
        visitRoute,
        visitStartRoute,
        visitRecordRoute,
        settingsRoute,
        billingRoute,
        billingUpdateRoute,
        billingCheckoutRoute,
        referralRoute,
        accountRoute,
        templatesRoute,
        templateFormRoute,
        developerRoute,
        requestFeatureRoute,
        unauthorizedRoute,
        snippetsRoute,
        isProtectedRoute,
        navAuthCheck,
        navSignIn,
        navSignUp,
        navSignUpCompletion,
        navForgotPassword,
        navResetPassword,
        navMain,
        navVisits,
        navVisit,
        navVisitStart,
        navVisitRecord,
        navSettings,
        navAccount,
        navTemplates,
        navTemplateForm,
        navDeveloper,
        navRequestFeature,
        navBilling,
        navReferral,
        navBillingUpdate,
        navBillingCheckout,
        navUnauthorized,
        navDictationRecord,
        navSnippets,
        dictationRecordRoute,
        dictationsRoute
    }
}
